import {
  DEFAULT_TIME_DISPLAY_MODE,
  TimeDisplayMode,
} from "@/shared/state/visualization/schema/v1/v1";
import {
  Duration,
  type FractionDigits,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
  nanoSecToUTCTimestamp,
  roundNsToFractionDigits,
} from "@/shared/time";

// The enum lives with the v1 viz schema because its string values are persisted
// vocabulary (`settings.timeDisplayMode` in stored workspace configs). It is
// re-exported here so display-layer consumers keep one import site.
export { DEFAULT_TIME_DISPLAY_MODE, TimeDisplayMode };

/** Fractional digits every mode but Local shows when nobody asks for fewer. */
const FULL_NANOSECOND_DIGITS: FractionDigits = 9;

/**
 * `Intl.DurationFormat` at full nanosecond width, for consumers that format
 * elapsed seconds outside {@link formatTimeByMode} (the plot tooltip). Null on
 * runtimes without `Intl.DurationFormat`; callers must handle that.
 *
 * The HH:MM:SS display mode itself no longer goes through this: see
 * {@link formatElapsedHms}.
 */
export const elapsedTimeFormatter: Intl.DurationFormat | null =
  "DurationFormat" in Intl
    ? new Intl.DurationFormat("en", {
        style: "digital",
        hours: "2-digit",
        fractionalDigits: FULL_NANOSECOND_DIGITS,
      })
    : null;

/**
 * Renders an elapsed nanosecond count as `HH:MM:SS` followed by
 * `fractionDigits` decimals (none, and no point, at zero), rounding half away
 * from zero (see {@link roundNsToFractionDigits}) before splitting into
 * components so a narrowed fraction carries into the second above it. Hours
 * grow past two digits rather than wrapping; a negative count, such as a chat
 * timestamp before the transcript's t=0, gets a leading minus.
 *
 * This is exactly what `Intl.DurationFormat("en", { style: "digital",
 * hours: "2-digit" })` prints, done by hand because that API is missing from
 * Node 22 (which CI runs) and from browsers older than Safari 18.4 and
 * Firefox 139, where the mode would otherwise silently degrade to plain
 * seconds.
 */
export function formatElapsedHms(
  elapsedNanos: bigint,
  fractionDigits: FractionDigits = FULL_NANOSECOND_DIGITS,
): string {
  const rounded = roundNsToFractionDigits(elapsedNanos, fractionDigits);
  // A negative that rounds away to zero renders unsigned, as `nanoSecToSecStr`
  // does. Split the magnitude so every component is non-negative.
  const negative = rounded < 0n;
  const { hours, minutes, seconds, nanoseconds } = Duration.from({
    nanoseconds: negative ? -rounded : rounded,
  }).toObject();
  const sign = negative ? "-" : "";
  const hms = [hours, minutes, seconds]
    .map((component) => String(component).padStart(2, "0"))
    .join(":");
  if (fractionDigits === 0) {
    return `${sign}${hms}`;
  }
  const fraction = String(nanoseconds)
    .padStart(FULL_NANOSECOND_DIGITS, "0")
    .slice(0, fractionDigits);
  return `${sign}${hms}.${fraction}`;
}

export const TIME_DISPLAY_MODES = [
  TimeDisplayMode.Elapsed,
  TimeDisplayMode.ElapsedTime,
  TimeDisplayMode.Log,
  TimeDisplayMode.Local,
  TimeDisplayMode.UTC,
] as const;

// Short labels for the timeline's mode picker and the workspace settings
// dialog, so both surfaces name the modes identically. The seconds variant is
// suffixed "(s)" to distinguish it from the HH:MM:SS variant, which keeps the
// unadorned "Elapsed" label. Decoupled from the enum string values so
// persisted configs (which store the enum values) remain stable.
export const TIME_DISPLAY_MODE_LABELS: Record<TimeDisplayMode, string> = {
  [TimeDisplayMode.Elapsed]: "Elapsed (s)",
  [TimeDisplayMode.ElapsedTime]: "Elapsed",
  [TimeDisplayMode.Log]: "Log",
  [TimeDisplayMode.Local]: "Local",
  [TimeDisplayMode.UTC]: "UTC",
};

/**
 * Formats a timestamp based on the current time display mode.
 *
 * @param time - The timestamp in nanoseconds
 * @param mode - The display mode to use
 * @param earliestTime - The earliest timestamp in the workspace (used for Elapsed mode)
 * @param includeTz - Whether to include the timezone in the output if local time is displayed (default: true)
 * @param fractionDigits - Decimal places to keep after the seconds. Omit for
 *   the visualizer's own widths, which differ per mode (nine for every mode
 *   but Local, three for that one) and are what every surface inside the
 *   workspace renders. The chat's timestamp settings pass a width so a
 *   transcript can narrow what the visualizer shows at full precision.
 * @returns A formatted string representation of the timestamp
 */
export function formatTimeByMode(
  time: bigint,
  mode: TimeDisplayMode,
  earliestTime: bigint | null | undefined,
  includeTz: boolean = true,
  fractionDigits?: FractionDigits,
): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
      return nanoSecToSecStr(time - (earliestTime ?? 0n), fractionDigits);
    case TimeDisplayMode.ElapsedTime:
      return formatElapsedHms(time - (earliestTime ?? 0n), fractionDigits);
    case TimeDisplayMode.Log:
      return nanoSecToSecStr(time, fractionDigits);
    case TimeDisplayMode.Local: {
      const raw = nanoSecToLocalTimestamp(time, fractionDigits);
      if (includeTz) {
        return raw;
      }
      // raw is like "2025-12-02 12:04:08.247 PM PST". The timezone is the last
      // space-separated field — en-US short zone names ("PST", "GMT+2") never
      // contain a space — and the trailing separator is kept, as the former
      // fixed [0, 26] slice did. Reading the boundary rather than pinning an
      // index is what lets the fraction be any width from zero to nine.
      return raw.slice(0, raw.lastIndexOf(" ") + 1);
    }
    case TimeDisplayMode.UTC:
      return nanoSecToUTCTimestamp(time, fractionDigits);
  }
}
